import { DateTime } from "luxon";
import React from "react";
import { Card, CardContent, Typography, Grid, Box, CardMedia } from '@mui/material';
import IbssSvgIcon from "../../Icons/SvgIcon/IbssSvgIcon";
import BuildingOverviewIcon from "../../Icons/BuildingOverviewIcon";
import CalendarIcon from "../../Icons/CalendarIcon";
import TimeIcon from "../../Icons/TimeIcon";
import CaretRightIcon from "../../Icons/CaretRightIcon";
import { appContext } from "../../../AppContext";
import { NodeHelper } from "../../../Common/NodeHelper";


class IbssEventCard extends React.Component<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private nodeHelper = new NodeHelper();

    private getBuildingName(nodeId: number): string
    {
        return this.nodeHelper.getBuildingByNode(nodeId)?.Name || '';
    }

    public render(): JSX.Element
    {
        const { title, description, itemDate, startTime, endTime, nodeId, imageURI, spaceName } = this.props;
        const formattedItemDate = itemDate.toLocaleDateWithWeekDayString();
        const formattedStartTime = startTime.toLocaleTimeString();
        const formattedEndTime = endTime?.toLocaleTimeString() ?? '';
        const timeLabel = formattedEndTime ? `${formattedStartTime} - ${formattedEndTime}` : formattedStartTime;
        const buildingName = this.getBuildingName(nodeId);

        return (
            <Card sx={{ display: 'flex',maxHeight:140, cursor: this.props.showDetails ? 'pointer' : 'inherit' }} onClick={() => { this.props.showDetails && this.props.navigateToDetails(this.props.nodeId, this.props.id) }}>
                {/* Left-side image */}
                <CardMedia
                    component="img"
                    sx={{ width: 160, minWidth: 160, objectFit: 'cover' }}
                    image={imageURI}
                    alt="event img"
                />
                {/* Right-side content */}
                <Box sx={{ display: 'flex', flexDirection: 'column', width: 'calc(100% - 160px)', justifyContent: 'center', overflow: 'hidden' }}>
                    <CardContent className="card-botom-spacing-0">
                        <Grid container spacing={2} sx={{ pb: 0 }}>
                            <Grid item xs={12} md={5} sx={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 2, // show ellipsis after 2 lines
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '100%',
                                        wordBreak: 'break-word', // break long words at any point
                                        whiteSpace: 'normal',    // allow wrapping
                                    }}
                                >
                                    {title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 2, // show ellipsis after 2 lines
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '100%',
                                        wordBreak: 'break-word', // break long words at any point
                                        whiteSpace: 'normal',    // allow wrapping
                                    }}
                                >
                                    {description}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
                                    <Box display={'flex'} gap={2} mb={1}>
                                        <IbssSvgIcon fontSize="small" sx={{ color: (theme) => theme.palette.text.primary }}>
                                            <BuildingOverviewIcon />
                                        </IbssSvgIcon>
                                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {buildingName} {spaceName ? `, ${spaceName}` : ''}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        {this.props.layout === 'News' && (
                                            <Box display={'flex'} gap={2} mb={1} color="textSecondary">
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                                                    {this.labels.funcPublished_S}:
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box display={'flex'} flexDirection={this.props.layout === 'News' ? 'row' : 'column'} gap={2} color="textSecondary">
                                            <Box display={'flex'} gap={1} color="textSecondary">
                                                <IbssSvgIcon fontSize="small">
                                                    <CalendarIcon />
                                                </IbssSvgIcon>
                                                <Typography variant="body2" >
                                                    {formattedItemDate}
                                                </Typography>
                                            </Box>
                                            <Box display={'flex'} gap={1}>
                                                <IbssSvgIcon fontSize="small">
                                                    <TimeIcon />
                                                </IbssSvgIcon>
                                                <Typography variant="body2">
                                                    {timeLabel}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={2} display={'flex'} alignItems={'top'}>
                                {this.props.status}
                            </Grid>
                            <Grid item xs={12} md={1} display={'flex'} alignItems={'center'} justifyContent={'center'}>
                                {this.props.showDetails &&
                                    <IbssSvgIcon
                                        sx={{ fontSize: '0.75rem', cursor: 'pointer', padding: '8px', '&:hover': { opacity: 0.8 } }}
                                    >
                                        <CaretRightIcon />
                                    </IbssSvgIcon>
                                }
                            </Grid>
                        </Grid>
                    </CardContent>
                </Box>
            </Card >
        );
    }
}

export default IbssEventCard;

interface IProps 
{
    id: string;
    title: string;
    description: string;
    actionURL: string;
    imageURI: string;
    itemDate: DateTime;
    startTime: DateTime;
    endTime?: DateTime;
    nodeId: number;
    status: React.ReactNode;
    fullDescription?: string;
    spaceName?: string;
    showDetails: boolean;
    layout?: 'News' | 'Events';
    navigateToDetails: (nodeId: number, id: string) => void;
}

interface IState
{

}
